/* Gunrange shop — product behaviour, for every product root
   ([data-gr-product]: the product page section, the quick-add content).
   All handlers are delegated from the document, so quick-add content and
   theme-editor re-renders work without re-binding.

   - Variant picker (gr-variant-picker): a new choice re-renders the root's
     section for the chosen option values (?option_values=…, Section
     Rendering API, GR.fetchSection), swaps every [data-gr-part], sets the
     form's variant id, points the availability component at the new variant
     (GR.availability) and shows the variant's image. The product page keeps
     ?variant= in the address bar. If the re-render fails, the browser goes
     to the product URL with those options chosen. A choice made before this
     deferred script ran (or restored by the browser) is applied on start.
   - Gallery (gr-product-gallery): thumbnails switch the image; the zoom
     layer shows the active image large.
   - Add to cart (gr-buy-buttons): POST /cart/add.js with the form (its
     _gr_availability property and a gift card's recipient fields included),
     re-rendering the cart sections (GR.cart.sectionIds(): header button,
     drawer, cart page) in the same request. Success: "Added" on the button,
     a note with a link to the cart, and `gr:cart-add` on document (detail:
     { item, sections, form }): gr-cart.js renders the sections and opens
     the drawer. A success that added fewer than asked (the line's total in
     the answer is below its quantity before, read from /cart.js just before
     the add, plus the quantity asked) says "Only N available" instead of
     "Added". A 422 for a variant Shopify now has as sold out shows the
     sold-out state (GR.availability.markSoldOut), not an error; any other
     refusal shows Shopify's message. After any refusal the cart sections
     are read back (GR.cart.refresh): a 422 above stock has already added
     what Shopify could.
   - Gift card recipient (gr-gift-card-recipient): the send-date field
     follows "Straight away / On a chosen date"; the time zone is filled in.
   - Back in stock (gr-notify): the contact form is posted in the background;
     it's posted again normally only when that didn't land (no network, a
     non-2xx, Shopify's challenge page), never twice.
   - Recommendation rails (gr-product-rail, [data-gr-recs][data-url]):
     fetched as they near the viewport, or once the page is idle after load,
     and swapped in. */
(() => {
  'use strict';

  const GR = window.GR;
  if (!GR || typeof GR.fetchSection !== 'function') {
    console.error('[gr-product] gr-shop.js has not loaded; product forms post normally.');
    return;
  }
  if (GR.product) return; // rendered once in the layout; guard a second copy anyway
  GR.product = {};

  const ADDED_MS = 2500;
  const availabilityOf = (root) => (GR.availability ? GR.availability.get(root.querySelector('[data-gr-avail]')) : null);

  /* ── Gallery ─────────────────────────────────────────────────────────── */
  const showMedia = (root, mediaId) => {
    const gallery = root.querySelector('[data-gr-gallery]');
    const slide = gallery && mediaId ? gallery.querySelector(`[data-gr-media="${CSS.escape(mediaId)}"]`) : null;
    if (!slide) return;
    gallery.querySelectorAll('[data-gr-media]').forEach((s) => {
      s.hidden = s !== slide;
    });
    gallery.querySelectorAll('[data-gr-thumb]').forEach((t) => {
      t.setAttribute('aria-pressed', String(t.dataset.grThumb === mediaId));
    });
    const zoom = gallery.querySelector('[data-gr-zoom]');
    if (zoom && slide.dataset.grZoomSrc) zoom.href = slide.dataset.grZoomSrc;
  };

  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-gr-thumb]');
    const root = thumb && thumb.closest('[data-gr-product]');
    if (root) showMedia(root, thumb.dataset.grThumb);
  });

  // The zoom layer (GR.layer, .zoomov) takes the active image when it opens.
  document.addEventListener('gr:layer-open', (e) => {
    const layer = e.target;
    if (!layer.classList.contains('zoomov')) return;
    const root = layer.closest('[data-gr-product]');
    const slide = root && root.querySelector('[data-gr-media]:not([hidden])');
    if (!slide) return;
    let img = layer.querySelector('img');
    if (!img) {
      img = document.createElement('img');
      layer.prepend(img);
    }
    const source = slide.querySelector('img');
    img.alt = source ? source.alt : '';
    img.src = slide.dataset.grZoomSrc;
  });

  /* ── Variant picker ──────────────────────────────────────────────────── */
  const loading = new WeakMap(); // root → AbortController of the re-render in flight

  const applyVariant = (root, next) => {
    // Keep keyboard focus on the same control across the swap.
    const focusedId = root.contains(document.activeElement) ? document.activeElement.id : '';
    root.querySelectorAll('[data-gr-part]').forEach((part) => {
      const replacement = next.querySelector(`[data-gr-part="${CSS.escape(part.dataset.grPart)}"]`);
      if (replacement) part.replaceWith(document.importNode(replacement, true));
    });
    if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

    const nextAvail = next.querySelector('[data-gr-avail]');
    const nextId = next.querySelector('[data-gr-variant-id]');
    const variantId = nextAvail ? nextAvail.dataset.variant || '' : '';

    const idInput = root.querySelector('[data-gr-variant-id]');
    if (idInput) {
      idInput.value = variantId;
      idInput.dataset.mediaId = nextId ? nextId.dataset.mediaId || '' : '';
      idInput.dispatchEvent(new Event('change', { bubbles: true })); // accelerated checkout reads it
    }
    const availability = availabilityOf(root);
    if (availability && nextAvail) availability.setVariant(nextAvail.dataset);
    else if (!availability) console.error('[gr-product] No availability component for this product; the buy button keeps its last state.');

    if (idInput && idInput.dataset.mediaId) showMedia(root, idInput.dataset.mediaId);

    const notifyVariant = root.querySelector('[data-gr-notify-variant]');
    const option = notifyVariant && variantId ? notifyVariant.querySelector(`option[data-variant="${CSS.escape(variantId)}"]`) : null;
    if (option) option.selected = true;

    root.querySelector('[data-gr-buy-msg]')?.replaceChildren();
    const error = root.querySelector('[data-gr-buy-error]');
    if (error) error.hidden = true;

    if (root.hasAttribute('data-gr-history') && variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState(window.history.state, '', url);
    }
  };

  const onPick = async (input) => {
    const root = input.closest('[data-gr-product]');
    const picker = input.closest('[data-gr-picker]');
    if (!root || !picker) return;
    const { grSection: sectionId, grUrl: productUrl } = root.dataset;
    if (!sectionId || !productUrl) {
      console.error('[gr-product] The product root has no data-gr-section / data-gr-url; cannot change the variant.');
      return;
    }
    const ids = Array.from(picker.querySelectorAll('input[type="radio"]:checked'), (i) => i.dataset.optionValueId).filter(Boolean);
    const url = `${productUrl}${productUrl.includes('?') ? '&' : '?'}option_values=${ids.join(',')}`;

    // Echo the choice at once; the re-render confirms it.
    const echo = input.closest('fieldset')?.querySelector('[data-gr-selected]');
    if (echo) echo.textContent = input.value;

    loading.get(root)?.abort();
    const controller = new AbortController();
    loading.set(root, controller);
    availabilityOf(root)?.pending();

    try {
      const doc = await GR.fetchSection(url, sectionId, { signal: controller.signal });
      const next = doc.querySelector('[data-gr-product]');
      if (!next) throw new Error(`The re-render of "${sectionId}" has no product root.`);
      applyVariant(root, next);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[gr-product] The variant could not be loaded here; opening it as a page.', error);
      window.location.assign(url);
    } finally {
      if (loading.get(root) === controller) loading.delete(root);
    }
  };

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-gr-picker] input[type="radio"]')) onPick(e.target);
  });

  // The picker shows as soon as html.gr-js is set, but this script is
  // deferred: a choice made before it ran fired its `change` to no one, and
  // so did a choice the browser restored (back, reload). Either one is a
  // checked radio the markup didn't check; apply it as a pick.
  const reconcilePickers = (scope) => {
    (scope || document).querySelectorAll('[data-gr-picker]').forEach((picker) => {
      const picked = Array.from(picker.querySelectorAll('input[type="radio"]:checked')).find((input) => !input.defaultChecked);
      if (picked) onPick(picked);
    });
  };
  reconcilePickers();
  document.addEventListener('gr:quick-add-loaded', (e) => reconcilePickers(e.target));
  document.addEventListener('shopify:section:load', (e) => reconcilePickers(e.target));

  /* ── Add to cart ─────────────────────────────────────────────────────── */
  const setLabel = (button, name) => {
    const label = GR.template(name);
    if (label) button.replaceChildren(label);
  };

  // Shopify's word on the variant right now; null when it can't be read.
  const shopifyHasSoldOut = async (root, variantId) => {
    try {
      const url = new URL(root.dataset.grUrl, window.location.origin);
      url.search = '';
      const response = await fetch(`${url.pathname}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const product = await response.json();
      const variant = (product.variants || []).find((v) => String(v.id) === String(variantId));
      return variant ? variant.available === false : null;
    } catch (error) {
      console.warn('[gr-product] Could not re-read the product after a refused add.', error);
      return null;
    }
  };

  // Each cart line's quantity by line key, read just before an add: the
  // add's answer gives the line's total after it, so what landed is that
  // total minus the line's quantity before. null when the cart can't be read.
  const lineQuantities = async (form) => {
    try {
      const url = `${form.getAttribute('action').replace(/\/add$/, '')}.js`; // routes.cart_add_url is <cart url>/add
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await response.json();
      return new Map((cart.items || []).map((item) => [item.key, Number(item.quantity) || 0]));
    } catch (error) {
      console.warn('[gr-product] Could not read the cart before adding; a capped add is caught only when the line holds fewer than asked.', error);
      return null;
    }
  };

  const showError = (form, message) => {
    const error = form.querySelector('[data-gr-buy-error]');
    if (!error) return;
    error.textContent = message || form.dataset.grError || '';
    error.hidden = false;
  };

  const addToCart = async (form) => {
    const root = form.closest('[data-gr-product]');
    const button = form.querySelector('[data-gr-buy]');
    const variantId = form.elements.id ? form.elements.id.value : '';
    if (!root || !button || !variantId) return;
    if (button.getAttribute('aria-disabled') === 'true' || button.hasAttribute('data-gr-busy')) return;
    if (!form.reportValidity()) return;
    // A tracked product's add carries the stock status the shopper saw
    // (properties[_gr_availability]; the cart flags backorder lines by it).
    const seen = form.querySelector('[data-gr-avail-prop]');
    if (seen && seen.disabled && root.querySelector('[data-gr-avail][data-tracked="true"]')) {
      console.error('[gr-product] Add to cart before the stock status is known; not sent.');
      return;
    }

    const message = form.querySelector('[data-gr-buy-msg]');
    const error = form.querySelector('[data-gr-buy-error]');
    if (message) message.replaceChildren();
    if (error) error.hidden = true;

    // Back to what the variant allows now: it may have changed, or its
    // stock check restarted, while the add was out (gr-availability's rules).
    const restore = () => {
      button.removeAttribute('data-gr-busy');
      const state = availabilityOf(root)?.state;
      if (!state || state.buyable) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', 'true');
      const key = state ? state.state : '';
      setLabel(button, key === 'checking' ? 'buy-checking' : key === 'unavailable' ? 'buy-unavailable' : 'buy-add');
    };
    button.setAttribute('data-gr-busy', '');
    button.setAttribute('aria-disabled', 'true');
    setLabel(button, 'buy-adding');

    const body = new FormData(form);
    // The cart sections (header button, drawer, cart page) come back rendered in the same response.
    const sections = GR.cart ? GR.cart.sectionIds() : [];
    if (sections.length) {
      body.append('sections', sections.join(','));
      body.append('sections_url', window.location.pathname);
    }

    const before = await lineQuantities(form);
    let response;
    let data = null;
    try {
      response = await fetch(`${form.getAttribute('action')}.js`, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      data = await response.json().catch(() => null);
    } catch (networkError) {
      console.error('[gr-product] Add to cart failed to reach the store.', networkError);
      restore();
      showError(form);
      if (GR.cart) GR.cart.refresh(); // the add may have landed before the answer was lost
      return;
    }

    if (response.ok) {
      if (!GR.cart) console.error('[gr-product] gr-cart.js has not loaded; the cart button and drawer keep their old state.');
      // The answer's quantity is the line's total after the add (the line
      // Shopify returned, by key: the same variant with other properties is
      // another line). Below what the line held before plus what was asked,
      // Shopify added fewer (it holds less stock). Without the cart read,
      // only a total below the quantity asked shows it.
      const asked = Number(form.elements.quantity ? form.elements.quantity.value : 1) || 1;
      const held = data ? Number(data.quantity) : NaN;
      const had = before && data && data.key ? before.get(data.key) || 0 : 0;
      const limited = Number.isFinite(held) && held < had + asked;
      document.dispatchEvent(new CustomEvent('gr:cart-add', { detail: { item: data, sections: (data && data.sections) || {}, form } }));
      if (limited) {
        restore();
        const note = GR.template('buy-limited-note');
        if (message && note) {
          const texts = document.createTreeWalker(note, NodeFilter.SHOW_TEXT);
          while (texts.nextNode()) texts.currentNode.nodeValue = texts.currentNode.nodeValue.replaceAll('{n}', String(held));
          message.replaceChildren(note);
        }
        return;
      }
      setLabel(button, 'buy-added');
      const note = GR.template('buy-added-note');
      if (message && note) message.replaceChildren(note);
      setTimeout(restore, ADDED_MS);
      return;
    }

    // A refusal can still have changed the cart: above stock, Shopify adds
    // what it can and answers 422 ("Only 50 items were added…"). The header
    // count and the drawer are read back from the store, never left as they were.
    const reread = GR.cart ? GR.cart.refresh() : Promise.resolve(false);

    if (response.status === 422 && (await shopifyHasSoldOut(root, variantId)) === true) {
      button.removeAttribute('data-gr-busy');
      if (GR.availability) GR.availability.markSoldOut(variantId);
      return;
    }

    console.error(`[gr-product] Add to cart was refused (HTTP ${response.status}).`, data);
    restore();
    showError(form, data && (data.description || data.message));
    if (!(await reread)) console.error('[gr-product] The cart could not be read back after a refused add; the header count may be out of date until the next page.');
  };

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-gr-buy-form]');
    if (!form || e.defaultPrevented) return; // gr-availability refuses a button that isn't buyable
    e.preventDefault();
    addToCart(form);
  });

  /* ── Gift card recipient (gr-gift-card-recipient) ─────────────────────── */
  // "On a chosen date" shows the date field and requires it; "Straight away"
  // hides and clears it, so a date picked earlier isn't sent.
  const syncGiftWhen = (select) => {
    const row = select.closest('[data-gr-gift]')?.querySelector('[data-gr-gift-date]');
    if (!row) return;
    const later = select.value === 'later';
    row.toggleAttribute('data-open', later);
    const date = row.querySelector('input');
    if (!date) return;
    date.required = later;
    if (!later) date.value = '';
  };

  // The shopper's time zone (minutes from UTC), so a send date means their day.
  const initGift = (scope) => {
    (scope || document).querySelectorAll('[data-gr-gift-offset]').forEach((input) => {
      input.value = String(new Date().getTimezoneOffset());
      input.disabled = false;
    });
    (scope || document).querySelectorAll('[data-gr-gift-when]').forEach(syncGiftWhen);
  };

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-gr-gift-when]')) syncGiftWhen(e.target);
  });
  initGift();
  document.addEventListener('gr:quick-add-loaded', (e) => initGift(e.target));
  document.addEventListener('shopify:section:load', (e) => initGift(e.target));

  /* ── Back in stock (Shopify contact form) ────────────────────────────── */
  // The request goes in the background. Posted again normally only when it
  // didn't land (no network, a non-2xx, or Shopify sent it to its challenge
  // page), so the shop never gets it twice. A page that comes back showing
  // the form's error (gr-notify renders it) was refused: the error shows in
  // place. Any other 2xx landed.
  const NOTIFY_ERROR = '[data-gr-notify-form] [data-gr-notify-error]:not([hidden])';

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-gr-notify-form]');
    if (!form || e.defaultPrevented) return;
    e.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;
    const show = (part) => {
      if (submit) submit.disabled = false;
      form.querySelector('[data-gr-notify-error]')?.toggleAttribute('hidden', part !== 'error');
      form.querySelector('[data-gr-notify-done]')?.toggleAttribute('hidden', part !== 'done');
      form.querySelector('[data-gr-notify-fields]')?.toggleAttribute('hidden', part === 'done');
    };
    let response;
    try {
      // URL-encoded, exactly what the form posts natively.
      response = await fetch(form.getAttribute('action'), {
        method: 'POST',
        body: new URLSearchParams(new FormData(form)),
        headers: { Accept: 'text/html' }
      });
      const landed = response.ok && !new URL(response.url).pathname.startsWith('/challenge');
      if (!landed) throw new Error(`the contact form answered HTTP ${response.status} at ${response.url}`);
    } catch (error) {
      console.warn('[gr-product] Back-in-stock request needs a normal form post.', error);
      if (submit) submit.disabled = false;
      form.submit();
      return;
    }
    let refused = false;
    if (new URL(response.url).searchParams.get('contact_posted') !== 'true') {
      try {
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        refused = !!doc.querySelector(NOTIFY_ERROR);
      } catch (error) {
        console.warn('[gr-product] Could not read the page the back-in-stock request came back to; taking it as sent.', error);
      }
    }
    if (refused) console.warn('[gr-product] Shopify refused the back-in-stock request; the form says so.');
    show(refused ? 'error' : 'done');
  });

  /* ── Recommendation rails ────────────────────────────────────────────── */
  const loadRecs = async (placeholder) => {
    try {
      const response = await fetch(placeholder.dataset.url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const rail = doc.querySelector('[data-gr-recs]');
      if (rail && !rail.hasAttribute('data-url')) {
        placeholder.replaceWith(document.importNode(rail, true));
        GR.layer.wire();
      } else {
        placeholder.remove(); // Shopify has no recommendations for this product
      }
    } catch (error) {
      console.warn('[gr-product] Recommendations could not be loaded; the rail is left out.', error);
      placeholder.remove();
    }
  };

  // A rail loads when it nears the viewport or once the page is idle after
  // `load`, whichever comes first. Scrolling alone isn't enough: a page that
  // is never scrolled (a full-page screenshot, a jump to the footer, a
  // crawler) would otherwise never show its recommendations.
  const watchRecs = (scope) => {
    const placeholders = Array.from((scope || document).querySelectorAll('[data-gr-recs][data-url]'));
    if (!placeholders.length) return;
    const started = new WeakSet();
    let observer = null;
    const start = (placeholder) => {
      if (started.has(placeholder)) return;
      started.add(placeholder);
      if (observer) observer.unobserve(placeholder);
      loadRecs(placeholder);
    };
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.isIntersecting && start(entry.target)),
        { rootMargin: '0px 0px 400px 0px' }
      );
      placeholders.forEach((p) => observer.observe(p));
    }
    const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
    const startAll = () => idle(() => placeholders.forEach(start), { timeout: 3000 });
    if (document.readyState === 'complete') startAll();
    else window.addEventListener('load', startAll, { once: true });
  };

  watchRecs();
  document.addEventListener('shopify:section:load', (e) => watchRecs(e.target));
})();
